import { createGlobalStore, getGlobalStore } from "@openmrs/esm-state";
import { shallowEqual } from "@openmrs/esm-utils";
const configInternalStoreInitialValue = {
    providedConfigs: [],
    schemas: {},
    moduleLoaded: {}
};
/**
 * @internal
 */ export const configInternalStore = createGlobalStore('config-internal', configInternalStoreInitialValue);
/** @internal */ export const temporaryConfigStore = createGlobalStore('temporary-config', {
    config: getTemporaryConfig()
});
temporaryConfigStore.subscribe((state)=>{
    setTemporaryConfig(state.config);
});
function setTemporaryConfig(value) {
    localStorage.setItem('openmrs:temporaryConfig', JSON.stringify(value));
}
function getTemporaryConfig() {
    try {
        return JSON.parse(localStorage.getItem('openmrs:temporaryConfig') || '{}');
    } catch (e) {
        return {};
    }
}
/** @internal */ export const configExtensionStore = createGlobalStore('config-store-of-extension-state', {
    mountedExtensions: []
});
function initializeConfigStore() {
    return {
        config: null,
        loaded: false,
        translationOverridesLoaded: false
    };
}
/**
 * Returns the configuration store for a specific module. Each module has its
 * own store that tracks the loading state and resolved configuration values.
 *
 * @param moduleName The name of the module whose config store to retrieve.
 * @returns A Zustand store containing the module's configuration state.
 *
 * @internal
 */ export function getConfigStore(moduleName) {
    // We use a store for each module's config, named `config-module-${moduleName}`
    return getGlobalStore(`config-module-${moduleName}`, initializeConfigStore());
}
/** @internal */ export function getExtensionSlotsConfigStore() {
    return getGlobalStore(`config-extension-slots`, {
        slots: {}
    });
}
/** @internal */ export function getExtensionSlotConfig(slotName) {
    return getExtensionSlotConfigFromStore(getExtensionSlotsConfigStore().getState(), slotName);
}
/** @internal */ export function getExtensionSlotConfigFromStore(state, slotName) {
    const slotConfig = state.slots[slotName];
    return slotConfig ?? {
        loaded: false,
        config: {}
    };
}
/**
 * One store for all the extensions
 * @internal
 */ export function getExtensionsConfigStore() {
    return getGlobalStore(`config-extensions`, {
        configs: {}
    });
}
/** @internal */ export function getExtensionConfig(slotName, extensionId) {
    if (typeof slotName !== 'string' || typeof extensionId !== 'string' || slotName === '__proto__' || extensionId === '__proto__' || slotName === 'constructor' || extensionId === 'constructor' || slotName === 'prototype' || extensionId === 'prototype') {
        throw new Error('Attempted to call `getExtensionConfig()` with invalid argument');
    }
    const extensionConfigStore = getExtensionsConfigStore();
    const selector = (configStore)=>configStore.configs[slotName]?.[extensionId];
    return {
        getInitialState () {
            return selector(extensionConfigStore.getInitialState());
        },
        getState () {
            return selector(extensionConfigStore.getState()) ?? {
                loaded: false,
                config: null
            };
        },
        setState (partial, replace = false) {
            extensionConfigStore.setState((state)=>{
                if (!state.configs[slotName]) {
                    state.configs[slotName] = {};
                }
                const newState = typeof partial === 'function' ? partial(state.configs.slotName[extensionId]) : partial;
                if (replace) {
                    state.configs[slotName][extensionId] = Object.assign({}, newState);
                } else {
                    state.configs[slotName][extensionId] = Object.assign({}, state.configs[slotName][extensionId], newState);
                }
                return state;
            });
        },
        subscribe (listener) {
            return extensionConfigStore.subscribe((state, prevState)=>{
                const newState = selector(state);
                const oldState = selector(prevState);
                if (!shallowEqual(newState, oldState)) {
                    listener(newState, oldState);
                }
            });
        },
        destroy () {
        /* this is a no-op */ }
    };
}
/** @internal */ export function getExtensionConfigFromStore(state, slotName, extensionId) {
    const extensionConfig = state.configs[slotName]?.[extensionId];
    return extensionConfig ?? {
        loaded: false,
        config: null
    };
}
/** @internal */ export function getExtensionConfigFromExtensionSlotStore(state, slotName, extensionId) {
    const extensionConfig = state.configure?.[extensionId];
    return extensionConfig ?? null;
}
/** @internal */ export const implementerToolsConfigStore = createGlobalStore('config-implementer-tools', {
    config: {}
});
